import { useCompactNumberFormatter } from '@shared/hooks/useCompactNumberFormatter';
import { AccommodationsConsoleState } from './AccommodationsConsole.types';
import { StaysSearchTerms } from '@apps/stays/types';

/**
 * Hook for formatting review counts in a compact way (e.g., 1000 -> 1K) using the current locale.
 */
export const useReviewCountFormatter = () => {
  return useCompactNumberFormatter().formatCompactNumber;
};

/**
 * Formats a user rating number to one decimal place.
 *
 * eg. 8.5 -> "8.5", 9 -> "9.0"
 */
export const formatUserRatingNumber = (rating: number): string => {
  return rating.toFixed(1);
};

export type ParamsUpdater = (key: string, value: string | null) => void;

export const createQueryParamsUpdater =
  (params: URLSearchParams): ParamsUpdater =>
  (key, value) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  };

export const getSearchFormInitialValue = (state: AccommodationsConsoleState): Partial<StaysSearchTerms> => {
  return {
    destination: state.searchLocation,
    lat: state.circularRegionSearch?.latitude,
    lng: state.circularRegionSearch?.longitude,
    startDate: {
      year: parseInt(state.searchDates.checkin.split('-')[0]),
      month: parseInt(state.searchDates.checkin.split('-')[1]),
      day: parseInt(state.searchDates.checkin.split('-')[2]),
    },
    endDate: {
      year: parseInt(state.searchDates.checkout.split('-')[0]),
      month: parseInt(state.searchDates.checkout.split('-')[1]),
      day: parseInt(state.searchDates.checkout.split('-')[2]),
    },
    rooms:
      state.occupancies?.occupancies?.map(occ => ({
        adults: occ.adults,
        childrenAges: (occ.childrenAges || []).filter((age): age is number => age !== null),
      })) || [],
  };
};

export const getIsValidLatitudeAndLongitude = (lat: number | undefined | null, lng: number | undefined | null): boolean => {
  if (lat === undefined || lat === null || lng === undefined || lng === null) {
    return false;
  }
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};
